import styled, { css } from 'styled-components';
import theme from 'styled-theming';
import { rgba } from 'polished';

import { breakpoints } from '@bitsoex/react-design-system';

import colors from '@legacy/moon/particles/colors';
import { FlexContainer } from '@legacy/moon/Layout';
import {
  H2 as MoonH2,
  TinyText as MoonTinyText,
  BodyLarge as MoonBodyLarge,
  BodyBase as MoonBodyBase,
  BodySmall,
} from '@legacy/moon/particles/Typography';
import MoonSelect from '@legacy/moon/atoms/Select';

import { DeltaProps } from './types';

const variants = theme('product', {
  light: css`
    border: 2px solid ${colors.aluminium};
    background: ${colors.moonlight0};
  `,
});

const Container = styled.div`
  width: 100%;
  display: grid;
  grid-auto-flow: row;
  ${variants};
  border-radius: 10px;
  padding: 16px;
  @media screen and (min-width: ${breakpoints.desktopSmall}) {
    padding: 20px;
  }
`;

const Header = styled.div`
  display: grid;
  grid-auto-flow: row;
`;

const Row = styled.div<{ isLoading?: boolean }>`
  display: inline-grid;
  grid-template-areas: 'price price' 'delta select';
  align-items: center;
  justify-content: space-between;
  gap: ${({ isLoading }) => (isLoading ? '0.5rem' : '0')};

  @media screen and (min-width: ${breakpoints.tabletLarge}) {
    grid-template-areas: 'price delta select';
    grid-auto-columns: auto 1fr 90px;
  }
`;

const BodyLarge = styled(MoonBodyLarge)`
  margin-bottom: 0;
  color: ${colors.moonlight60};
`;
const PriceRow = styled(FlexContainer)`
  grid-area: price;
  margin-right: 1rem;
`;

const Price = styled(MoonH2)`
  margin-right: 8px;
  margin-bottom: 0;
  color: ${colors.moonlight90};
  font-weight: 700;
`;

const PriceCode = styled(MoonH2)`
  margin-bottom: 0;
  color: ${colors.moonlight60};
  font-weight: 700;
  text-transform: uppercase;
`;

const Delta = styled(MoonBodyBase)<DeltaProps>`
  grid-area: delta;
  flex: 0 0 auto;
  font-size: 1rem;
  line-height: 15px;
  color: ${({ isDeltaPositive }) => (isDeltaPositive ? colors.borealis60 : colors.mars60)};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 0;

  &:after {
    content: ' ';
    display: block;
    height: 0;
    width: 0;
    border-width: 5px 4px;
    border-style: solid;
    border-color: transparent;
    ${({ isDeltaPositive }) =>
      isDeltaPositive ? `border-bottom-color: ${colors.borealis60}` : `border-top-color: ${colors.mars60}`};
    margin-top: ${({ isDeltaPositive }) => (isDeltaPositive ? '-5px' : '5px')};
    margin-left: 6px;
  }
`;

const Select = styled(MoonSelect)`
  grid-area: select;
  min-width: 90px;
  margin-bottom: 0;
`;

const EmptyCol = styled.div``;

const Body = styled.div<{ isReady: boolean }>`
  ${({ isReady }) =>
    isReady
      ? `height: 106px;
        @media screen and (min-width: ${breakpoints.tabletSmall}) {
          height: 160px;
        }
        @media screen and (min-width: ${breakpoints.desktopSmall}) {
          height: 270px;
        }`
      : 'height: unset;'};
`;

const TooltipContainer = styled(FlexContainer)`
  flex-flow: column wrap;
  background-color: ${colors.moonlight0};
  padding: 0.5rem 1rem;
  box-shadow: 0px 0px 16px ${rgba(colors.moonlight40, 0.3)};
`;

const ValueText = styled(BodySmall)`
  color: ${colors.moonlight90};
  margin-bottom: 0;
`;

const LabelText = styled(MoonTinyText)`
  color: ${colors.moonlight60};
`;

export {
  Container,
  Header,
  Row,
  BodyLarge,
  PriceRow,
  Price,
  PriceCode,
  Delta,
  Select,
  EmptyCol,
  TooltipContainer,
  ValueText,
  LabelText,
  Body,
};
